import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Link as LinkIcon,
  Copy,
  Trash2,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";
import { studioApi } from "@/lib/api";

const TTL_OPTIONS = [
  { value: "1", label: "1 day" },
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
];

// Compute a friendly time-until-expiry string. Returns 'expired' / 'in 6 days' etc.
const timeUntil = (isoUtc) => {
  if (!isoUtc) return "—";
  const exp = new Date(isoUtc).getTime();
  const now = Date.now();
  const ms = exp - now;
  if (ms <= 0) return "expired";
  const days = Math.floor(ms / 86400000);
  if (days >= 1) return `in ${days} day${days === 1 ? "" : "s"}`;
  const hours = Math.floor(ms / 3600000);
  if (hours >= 1) return `in ${hours} hr${hours === 1 ? "" : "s"}`;
  return "<1 hr";
};

const buildShareUrl = (token) => {
  const base = typeof window !== "undefined" ? window.location.origin : "";
  return `${base}/share/studio/${encodeURIComponent(token)}`;
};

export default function ShareLinkDialog({
  open,
  onOpenChange,
  defaultStart,
  defaultEnd,
  availableProjects = [],
  defaultProjectId = "all",
  capacityHoursPerDay = 8,
}) {
  // Create form state
  const [name, setName] = useState("");
  const [start, setStart] = useState(defaultStart || "");
  const [end, setEnd] = useState(defaultEnd || "");
  const [projectId, setProjectId] = useState(defaultProjectId);
  const [ttlDays, setTtlDays] = useState("7");
  const [capacity, setCapacity] = useState(capacityHoursPerDay);
  const [creating, setCreating] = useState(false);

  // Links list
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [newlyCreated, setNewlyCreated] = useState(null);

  const loadLinks = async () => {
    setLoadingLinks(true);
    try {
      const data = await studioApi.listShareLinks();
      setLinks(data.links || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load share links");
    } finally {
      setLoadingLinks(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setStart(defaultStart || "");
    setEnd(defaultEnd || "");
    setProjectId(defaultProjectId);
    setCapacity(capacityHoursPerDay);
    setName("");
    setTtlDays("7");
    setNewlyCreated(null);
    loadLinks();
  }, [open, defaultStart, defaultEnd, defaultProjectId, capacityHoursPerDay]);

  const handleCreate = async () => {
    if (!start || !end) {
      toast.error("Date range required");
      return;
    }
    setCreating(true);
    try {
      const body = {
        name: name.trim() || undefined,
        start,
        end,
        ttl_days: Number(ttlDays),
        capacity_hours_per_day: Number(capacity) || 8,
        project_ids: projectId && projectId !== "all" ? [projectId] : [],
      };
      const link = await studioApi.createShareLink(body);
      setLinks((prev) => [link, ...prev]);
      setNewlyCreated(link);
      const url = buildShareUrl(link.token);
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Share link created & copied to clipboard");
      } catch {
        toast.success("Share link created");
      }
      setName("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create share link");
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async (link) => {
    try {
      await navigator.clipboard.writeText(buildShareUrl(link.token));
      toast.success("Copied");
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  const handleRevoke = async (link) => {
    if (!window.confirm(`Revoke "${link.name}"? Anyone with this link will get a 410.`)) return;
    setRevokingId(link.id);
    try {
      await studioApi.revokeShareLink(link.id);
      setLinks((prev) => prev.map((l) => (l.id === link.id ? { ...l, revoked: true } : l)));
      toast.success("Link revoked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke");
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-[640px]"
        data-testid="share-link-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-amber-500" />
            Share Studio view
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Token-protected, time-limited public URL for the Studio Gantt.
            Data stays live within the link&apos;s TTL — clients see the
            current snapshot every time they open it.
          </DialogDescription>
        </DialogHeader>

        {/* ── Create form ────────────────────────────────────── */}
        <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-4 space-y-3">
          <div className="text-overline text-zinc-500">Create new link</div>
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-500" htmlFor="share-name">Label (optional)</Label>
            <Input
              id="share-name"
              data-testid="share-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. October crew load for Whiplash brand"
              className="bg-zinc-950 border-zinc-800 h-8 text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-500" htmlFor="share-start">Start</Label>
              <Input
                id="share-start"
                type="date"
                data-testid="share-start-input"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="bg-zinc-950 border-zinc-800 h-8 text-sm font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-500" htmlFor="share-end">End</Label>
              <Input
                id="share-end"
                type="date"
                data-testid="share-end-input"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="bg-zinc-950 border-zinc-800 h-8 text-sm font-mono"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-500">Project filter</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger
                  data-testid="share-project-select"
                  className="bg-zinc-950 border-zinc-800 focus:ring-amber-500 h-8 text-xs"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[260px]">
                  <SelectItem value="all" className="focus:bg-zinc-900">All projects</SelectItem>
                  {availableProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id} className="focus:bg-zinc-900">
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-500">Expires in</Label>
              <Select value={ttlDays} onValueChange={setTtlDays}>
                <SelectTrigger
                  data-testid="share-ttl-select"
                  className="bg-zinc-950 border-zinc-800 focus:ring-amber-500 h-8 text-xs"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  {TTL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} className="focus:bg-zinc-900">
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-500" htmlFor="share-cap">Capacity h/day</Label>
              <Input
                id="share-cap"
                type="number"
                min={1}
                max={24}
                data-testid="share-capacity-input"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                className="bg-zinc-950 border-zinc-800 h-8 text-xs font-mono"
              />
            </div>
          </div>
          <div className="flex items-center justify-end">
            <Button
              type="button"
              size="sm"
              onClick={handleCreate}
              disabled={creating || !start || !end}
              className="bg-amber-500 hover:bg-amber-400 text-black"
              data-testid="share-create-btn"
            >
              {creating && <Loader2 className="w-3 h-3 mr-2 animate-spin" />}
              Create link
            </Button>
          </div>
          {newlyCreated && (
            <div
              data-testid="share-new-link-banner"
              className="mt-2 rounded-sm border border-amber-700/60 bg-amber-950/30 px-3 py-2 flex items-center gap-2"
            >
              <span className="text-xs text-amber-200 font-mono truncate flex-1">
                {buildShareUrl(newlyCreated.token)}
              </span>
              <button
                type="button"
                onClick={() => handleCopy(newlyCreated)}
                className="text-amber-400 hover:text-amber-300 p-1"
                title="Copy"
                data-testid="share-new-copy-btn"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
              <a
                href={buildShareUrl(newlyCreated.token)}
                target="_blank"
                rel="noreferrer"
                className="text-amber-400 hover:text-amber-300 p-1"
                title="Open"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          )}
        </div>

        {/* ── Active links list ──────────────────────────────── */}
        <div className="rounded-md border border-zinc-800 bg-zinc-950/60 mt-1">
          <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
            <div className="text-overline text-zinc-500">Active links</div>
            <div className="text-[10px] font-mono text-zinc-600">{links.length}</div>
          </div>
          <div className="max-h-[260px] overflow-auto" data-testid="share-links-list">
            {loadingLinks ? (
              <div className="py-8 flex items-center justify-center">
                <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
              </div>
            ) : links.length === 0 ? (
              <div className="py-8 text-center text-xs text-zinc-500">
                No links yet
              </div>
            ) : (
              <ul className="divide-y divide-zinc-800">
                {links.map((link) => {
                  const expired = link.is_expired || link.revoked;
                  return (
                    <li
                      key={link.id}
                      data-testid={`share-link-row-${link.id}`}
                      className={`px-3 py-2.5 flex items-center gap-3 ${
                        expired ? "opacity-50" : ""
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-zinc-100 truncate flex items-center gap-2">
                          {link.name}
                          {link.revoked && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono text-rose-400 border border-rose-900/60 rounded-sm px-1.5">
                              <AlertTriangle className="w-2.5 h-2.5" />
                              revoked
                            </span>
                          )}
                          {!link.revoked && link.is_expired && (
                            <span className="text-[10px] font-mono text-zinc-500 border border-zinc-700 rounded-sm px-1.5">
                              expired
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] font-mono text-zinc-500 mt-0.5 flex flex-wrap items-center gap-x-2">
                          <span>{link.filters?.start} → {link.filters?.end}</span>
                          <span>·</span>
                          <span>{timeUntil(link.expires_at)}</span>
                          <span>·</span>
                          <span>{link.view_count || 0} view{link.view_count === 1 ? "" : "s"}</span>
                          {link.filters?.project_ids?.length > 0 && (
                            <>
                              <span>·</span>
                              <span>{link.filters.project_ids.length} project</span>
                            </>
                          )}
                        </div>
                      </div>
                      {!link.revoked && (
                        <>
                          <button
                            type="button"
                            data-testid={`share-link-copy-${link.id}`}
                            onClick={() => handleCopy(link)}
                            className="text-zinc-500 hover:text-amber-500 p-1 transition-colors"
                            title="Copy URL"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                          <a
                            href={buildShareUrl(link.token)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`share-link-open-${link.id}`}
                            className="text-zinc-500 hover:text-amber-500 p-1 transition-colors"
                            title="Open"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                          <button
                            type="button"
                            data-testid={`share-link-revoke-${link.id}`}
                            onClick={() => handleRevoke(link)}
                            disabled={revokingId === link.id}
                            className="text-zinc-500 hover:text-rose-400 p-1 transition-colors"
                            title="Revoke"
                          >
                            {revokingId === link.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-zinc-400"
            data-testid="share-close-btn"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
